import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Button } from '@progress/kendo-react-buttons'
import { FaFileExcel, FaSearch } from 'react-icons/fa'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { appendEmployeeIds } from '../../data/reportUserScopeData'
import ReportUserFilter, { useReportUserFilter } from '../../components/ReportUserFilter/ReportUserFilter'
import { useToast } from '../../contexts/ToastContext'
import './QuoteJobSummary.css'

interface SummaryMonth {
  monthStart: string
  month: string
  count: number
  value: number
  priorYearCount: number
  priorYearValue: number
}

interface SummaryWorktype {
  worktypegroupId: number
  description: string
  colourHex: string | null
  count: number
  value: number
  countPct: number
}

interface SummaryKpis {
  kpiDate: string
  yearLabel: string
  yearCount: number
  yearValue: number
  monthLabel: string
  monthCount: number
  monthValue: number
  weekLabel: string
  weekCount: number
  weekValue: number
}

interface SummarySection {
  months: SummaryMonth[]
  worktypes: SummaryWorktype[]
  kpis: SummaryKpis
  totalCount: number
  totalValue: number
}

interface QuoteJobSummary {
  quotes: SummarySection
  jobs: SummarySection
}

const EMPTY_KPIS: SummaryKpis = {
  kpiDate: '',
  yearLabel: '',
  yearCount: 0,
  yearValue: 0,
  monthLabel: '',
  monthCount: 0,
  monthValue: 0,
  weekLabel: '',
  weekCount: 0,
  weekValue: 0,
}

const EMPTY_SECTION: SummarySection = {
  months: [],
  worktypes: [],
  kpis: EMPTY_KPIS,
  totalCount: 0,
  totalValue: 0,
}

const EMPTY_REPORT: QuoteJobSummary = { quotes: EMPTY_SECTION, jobs: EMPTY_SECTION }

type TabKey = 'quotes' | 'jobs'

// Every label on the page is derived from the tab rather than branched on, so
// the two halves cannot drift apart the way two copied components would.
const TABS: Record<TabKey, { label: string; noun: string; plural: string }> = {
  quotes: { label: 'Quotes', noun: 'Quote', plural: 'Quotes' },
  jobs: { label: 'Jobs', noun: 'Job', plural: 'Jobs' },
}

const formatCurrency = (value: number) =>
  `£${(Number(value) || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

// Full precision belongs in tiles and tooltips; the compact form keeps axis
// ticks readable at chart width.
const formatCompactCurrency = (value: number) => {
  const amount = Number(value) || 0
  if (amount >= 1_000_000) return `£${(amount / 1_000_000).toFixed(1)}m`
  if (amount >= 1_000) return `£${Math.round(amount / 1_000)}k`
  return `£${Math.round(amount)}`
}

// Recessive ink for axes - deliberately not a series colour, so axis text never
// reads as an identity.
const axisTick = { fontSize: '9pt', fill: '#5a6472' }

// Two series per chart, so colour is doing identity work here in a way it was
// not on the single-series reports. The current year carries the measure's
// colour - blue for counts, the green the grids already use for money - and the
// prior year is deliberately a recessive grey: it is a reference line, not a
// peer category. Both pairs clear the CVD separation threshold comfortably
// (deltaE 21.6 and 16.6 under protanopia, against a target of 8), and the prior
// year is dashed as well as grey so identity never rests on colour alone.
const COLOUR_COUNT = '#35529c'
const COLOUR_VALUE = '#217346'
const COLOUR_PRIOR = '#8a93a3'

// Work type groups carry their own colour from the database so the pie matches
// how a group is coloured everywhere else in the portal. Groups with no colour
// set fall back to this fixed order - never cycled past its length, because a
// ninth slice takes grey rather than a repeat that would read as a duplicate.
const FALLBACK_SLICE_COLOURS = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948',
]

const sliceColour = (worktype: SummaryWorktype, index: number) => {
  const own = (worktype.colourHex || '').trim()
  if (/^#[0-9a-fA-F]{6}$/.test(own)) return own
  return FALLBACK_SLICE_COLOURS[index] || '#8a93a3'
}

const toLocalDateString = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function QuoteJobSummary() {
  const navigate = useNavigate()
  const { showToast } = useToast()

  // A year back to today, matching the range the source report opens on.
  const today = new Date()
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(today.getFullYear() - 1, today.getMonth(), 1)
  )
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [kpiDate, setKpiDate] = useState<Date | null>(new Date())
  const [report, setReport] = useState<QuoteJobSummary>(EMPTY_REPORT)
  const [tab, setTab] = useState<TabKey>('quotes')
  const [hasRun, setHasRun] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const users = useReportUserFilter(hasPermission)

  const apiURL = process.env.REACT_APP_API_URL

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        if (currentUser && currentUser.permissions) {
          // An administrator gets in without the report role being assigned,
          // matching the policy the API endpoint is trimmed with.
          const permitted = currentUser.permissions.some(
            (p) =>
              p.assigned &&
              ((p.area === 'Portal' && p.permission === 'Administrator') ||
                (p.area === 'Reports' && p.permission === 'QuoteJobSummary'))
          )
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast])

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      showToast('Please select both start and end dates', 'info')
      return
    }
    if (startDate > endDate) {
      showToast('Start date must be before end date', 'error')
      return
    }
    try {
      setLoading(true)
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('Authorization token not found')

      // Built from the local date parts rather than toISOString(), which would
      // shift a UK date back a day over BST.
      // Quotes are narrowed by who they are assigned to, jobs by project
      // manager.
      const params = appendEmployeeIds(
        new URLSearchParams({
          startDate: toLocalDateString(startDate),
          endDate: toLocalDateString(endDate),
          kpiDate: toLocalDateString(kpiDate || new Date()),
        }),
        users.selectedIds
      )

      const response = await axios.get<QuoteJobSummary>(
        `https://${apiURL}/Reports/QuoteJobSummary?${params}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
          },
        }
      )
      setReport(response.data)
      setHasRun(true)
      showToast(
        `Loaded ${response.data.quotes.totalCount} quotes and ${response.data.jobs.totalCount} jobs`,
        'success'
      )
    } catch (error: any) {
      console.error('Error fetching quote and job summary:', error)
      const msg = error.response?.data?.message || error.message || 'Error loading the summary'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, kpiDate, users.selectedIds, showToast, apiURL])

  // Loaded once, as soon as permission is confirmed: the report opens on a
  // sensible range, so making the user press Search to see anything would be
  // an empty screen for no reason. Held back until the user filter has its
  // default, so the first load is the one the filter shows.
  useEffect(() => {
    if (hasPermission && users.ready && !hasRun) fetchData()
  }, [hasPermission, users.ready, hasRun, fetchData])

  const section = report[tab]
  const labels = TABS[tab]

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new()

    ;(['quotes', 'jobs'] as TabKey[]).forEach((key) => {
      const data = report[key]
      const name = TABS[key].plural

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(
          data.months.map((m) => ({
            'Month': m.month,
            [`${name} Created`]: m.count,
            [`${name} Created (Prior Year)`]: m.priorYearCount,
            'Value': Number(m.value),
            'Value (Prior Year)': Number(m.priorYearValue),
          }))
        ),
        `${name} by Month`
      )

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(
          data.worktypes.map((w) => ({
            'Work Type': w.description,
            [name]: w.count,
            'Share (%)': w.countPct,
            'Value': Number(w.value),
          }))
        ),
        `${name} by Work Type`
      )
    })

    const fileName = `quote_job_summary_${toLocalDateString(startDate || new Date())}_to_${toLocalDateString(endDate || new Date())}.xlsx`
    XLSX.writeFile(workbook, fileName)
    showToast(`Exported the summary to ${fileName}`, 'success')
  }

  // A range inside one month has nothing to trend, so the charts are dropped
  // rather than drawn as a lone point.
  const showTrend = section.months.length > 1
  // A long range would collide every month label against its neighbour, so past
  // a couple of years the axis thins out and the tooltip carries the rest.
  const monthTickInterval = section.months.length > 18
    ? Math.ceil(section.months.length / 12) - 1
    : 0

  // Two series, so a legend is always present. The last point is also labelled
  // directly, which is where the eye lands on a trend and saves a trip to the
  // legend for the reading that matters most.
  const endLabel = (text: string, colour: string, format: (v: number) => string) =>
    (props: any) => {
      const { x, y, index, value } = props
      // Recharts types a label renderer as always returning an element, so a
      // skipped point is an empty group rather than null.
      if (index !== section.months.length - 1) return <g />
      return (
        <text x={x} y={y} dx={6} dy={4} fill={colour} fontSize="9pt" fontWeight={600}>
          {`${text} ${format(value)}`}
        </text>
      )
    }

  const trendTooltip = (format: (v: number) => string) => (props: any) => {
    const { active, payload, label } = props
    if (!active || !payload?.length) return null
    return (
      <div className="quote-job-summary__tooltip">
        <div className="quote-job-summary__tooltip-title">{label}</div>
        {payload.map((entry: any) => (
          <div key={entry.dataKey} className="quote-job-summary__tooltip-row">
            <span
              className="quote-job-summary__tooltip-swatch"
              style={{ background: entry.color }}
            />
            <span>{entry.name}</span>
            <strong>{format(entry.value)}</strong>
          </div>
        ))}
      </div>
    )
  }

  if (!hasPermission) return null

  return (
    <div>
      <div style={{ padding: '1rem' }}>
        <div className="quote-job-summary__header">
          <h1>Quote &amp; Job Summary</h1>
        </div>

        {/* Filters */}
        <div className="quote-job-summary__filters">
          <div>
            <label className="quote-job-summary__filter-label">Start Date</label>
            <DatePicker
              value={startDate}
              onChange={(e) => setStartDate(e.value)}
              format="dd/MM/yyyy"
              width={160}
            />
          </div>
          <div>
            <label className="quote-job-summary__filter-label">End Date</label>
            <DatePicker
              value={endDate}
              onChange={(e) => setEndDate(e.value)}
              format="dd/MM/yyyy"
              width={160}
            />
          </div>
          <div>
            <label className="quote-job-summary__filter-label">KPI Date</label>
            <DatePicker
              value={kpiDate}
              onChange={(e) => setKpiDate(e.value)}
              format="dd/MM/yyyy"
              width={160}
            />
          </div>
          <ReportUserFilter
            scope={users.scope}
            value={users.selectedIds}
            onChange={users.setSelectedIds}
            disabled={loading}
            label="Users"
            labelClassName="quote-job-summary__filter-label"
          />
          <div>
            <Button onClick={fetchData} disabled={loading} themeColor="primary">
              <FaSearch style={{ marginRight: '0.4rem' }} />
              {loading ? 'Loading…' : 'Search'}
            </Button>
          </div>
          <div>
            <Button onClick={exportToExcel} disabled={!hasRun} data-excel="true">
              <FaFileExcel style={{ marginRight: '0.4rem' }} />
              Export to Excel
            </Button>
          </div>
        </div>

        {/* The two pages of the source report, as tabs */}
        <div className="quote-job-summary__tabs" role="tablist">
          {(Object.keys(TABS) as TabKey[]).map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              className={
                tab === key
                  ? 'quote-job-summary__tab quote-job-summary__tab--active'
                  : 'quote-job-summary__tab'
              }
              onClick={() => setTab(key)}
            >
              {TABS[key].label}
            </button>
          ))}
        </div>

        {/* KPI tiles - each covers the whole period containing the KPI date */}
        <div className="quote-job-summary__kpis">
          <div className="quote-job-summary__kpi quote-job-summary__kpi--navy">
            <div className="quote-job-summary__kpi-label">{labels.plural} this year</div>
            <div className="quote-job-summary__kpi-value">{section.kpis.yearCount}</div>
            <div className="quote-job-summary__kpi-sub">
              {section.kpis.yearLabel} · {formatCurrency(section.kpis.yearValue)}
            </div>
          </div>
          <div className="quote-job-summary__kpi quote-job-summary__kpi--green">
            <div className="quote-job-summary__kpi-label">{labels.plural} this month</div>
            <div className="quote-job-summary__kpi-value">{section.kpis.monthCount}</div>
            <div className="quote-job-summary__kpi-sub">
              {section.kpis.monthLabel} · {formatCurrency(section.kpis.monthValue)}
            </div>
          </div>
          <div className="quote-job-summary__kpi quote-job-summary__kpi--orange">
            <div className="quote-job-summary__kpi-label">{labels.plural} this week</div>
            <div className="quote-job-summary__kpi-value">{section.kpis.weekCount}</div>
            <div className="quote-job-summary__kpi-sub">
              {section.kpis.weekLabel} · {formatCurrency(section.kpis.weekValue)}
            </div>
          </div>
        </div>

        {/* Trends */}
        {showTrend && (
          <div className="quote-job-summary__charts">
            <div className="quote-job-summary__chart">
              <h3>{labels.plural} Created</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={section.months} margin={{ top: 10, right: 70, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eef1f4" vertical={false} />
                  <XAxis dataKey="month" tick={axisTick} interval={monthTickInterval} />
                  <YAxis tick={axisTick} allowDecimals={false} />
                  <Tooltip content={trendTooltip((v) => `${v}`)} />
                  <Legend wrapperStyle={{ fontSize: '9pt' }} />
                  <Line
                    type="monotone"
                    dataKey="priorYearCount"
                    name="Prior year"
                    stroke={COLOUR_PRIOR}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="This year"
                    stroke={COLOUR_COUNT}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    label={endLabel('', COLOUR_COUNT, (v) => `${v}`)}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="quote-job-summary__chart">
              <h3>{labels.noun} Value</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={section.months} margin={{ top: 10, right: 70, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eef1f4" vertical={false} />
                  <XAxis dataKey="month" tick={axisTick} interval={monthTickInterval} />
                  <YAxis tick={axisTick} tickFormatter={formatCompactCurrency} />
                  <Tooltip content={trendTooltip(formatCurrency)} />
                  <Legend wrapperStyle={{ fontSize: '9pt' }} />
                  <Line
                    type="monotone"
                    dataKey="priorYearValue"
                    name="Prior year"
                    stroke={COLOUR_PRIOR}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="This year"
                    stroke={COLOUR_VALUE}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    label={endLabel('', COLOUR_VALUE, formatCompactCurrency)}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Work type split. The pie answers "what is the shape of the work";
            the table beside it carries the figures the pie cannot label. */}
        <div className="quote-job-summary__charts">
          <div className="quote-job-summary__chart">
            <h3>{labels.plural} by Work Type</h3>
            {section.worktypes.length === 0 ? (
              <p className="quote-job-summary__empty">
                No {labels.plural.toLowerCase()} in the chosen range.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={section.worktypes}
                    dataKey="count"
                    nameKey="description"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={1}
                    stroke="#ffffff"
                    strokeWidth={2}
                  >
                    {section.worktypes.map((w, i) => (
                      <Cell key={w.worktypegroupId} fill={sliceColour(w, i)} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any, name: any) => [`${value} ${labels.plural.toLowerCase()}`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: '9pt' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="quote-job-summary__chart">
            <h3>{labels.plural} by Work Type</h3>
            <table className="quote-job-summary__table">
              <thead>
                <tr>
                  <th>Work Type</th>
                  <th>{labels.plural}</th>
                  <th>Share</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {section.worktypes.map((w, i) => (
                  <tr key={w.worktypegroupId}>
                    <td>
                      <span
                        className="quote-job-summary__swatch"
                        style={{ background: sliceColour(w, i) }}
                      />
                      {w.description}
                    </td>
                    <td className="quote-job-summary__num">{w.count}</td>
                    <td className="quote-job-summary__num">{w.countPct}%</td>
                    <td className="quote-job-summary__num">{formatCurrency(w.value)}</td>
                  </tr>
                ))}
                <tr className="quote-job-summary__total-row">
                  <td>Total</td>
                  <td className="quote-job-summary__num">{section.totalCount}</td>
                  <td className="quote-job-summary__num">100%</td>
                  <td className="quote-job-summary__num">{formatCurrency(section.totalValue)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuoteJobSummary
